import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Save, Trash2, Power, Zap, Clock, Calendar, Loader2, X, Edit2, Star, AlertCircle, ArrowRight } from 'lucide-react';
import PageHeader from './ui-shared/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['red', 'orange', 'amber', 'yellow', 'lime', 'emerald', 'teal', 'cyan', 'sky', 'blue', 'indigo', 'violet', 'fuchsia', 'pink', 'rose', 'slate'];
const COLOR_BG = {
  red: 'bg-gradient-to-br from-red-500 to-red-600',
  orange: 'bg-gradient-to-br from-orange-500 to-orange-600',
  amber: 'bg-gradient-to-br from-amber-500 to-amber-600',
  yellow: 'bg-gradient-to-br from-yellow-500 to-yellow-600',
  lime: 'bg-gradient-to-br from-lime-500 to-lime-600',
  emerald: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
  teal: 'bg-gradient-to-br from-teal-500 to-teal-600',
  cyan: 'bg-gradient-to-br from-cyan-500 to-cyan-600',
  sky: 'bg-gradient-to-br from-sky-500 to-sky-600',
  blue: 'bg-gradient-to-br from-blue-500 to-blue-600',
  indigo: 'bg-gradient-to-br from-indigo-500 to-indigo-600',
  violet: 'bg-gradient-to-br from-violet-500 to-violet-600',
  fuchsia: 'bg-gradient-to-br from-fuchsia-500 to-fuchsia-600',
  pink: 'bg-gradient-to-br from-pink-500 to-pink-600',
  rose: 'bg-gradient-to-br from-rose-500 to-rose-600',
  slate: 'bg-gradient-to-br from-slate-500 to-slate-600',
};
const DAYS = [
  { v: 0, label: 'Mon' }, { v: 1, label: 'Tue' }, { v: 2, label: 'Wed' },
  { v: 3, label: 'Thu' }, { v: 4, label: 'Fri' }, { v: 5, label: 'Sat' }, { v: 6, label: 'Sun' },
];

const blank = {
  key: '', name: '', description: '', eta_min: 30, eta_max: 60,
  factor: 1.0, enabled: true, color: 'blue', icon: '◆', order: 99,
  unavailable_schedule: [], offline: false, offline_message: '', is_default: false,
  time_to_open: 0, time_to_close: 0, disable_from: [], disable_to: [],
  always_available: false,
};

const fmtETA = (mn, mx) => {
  if (mn >= 60 && mx >= 60) return `${(mn/60).toFixed(mn%60===0?0:1)}–${(mx/60).toFixed(mx%60===0?0:1)} h`;
  if (mx >= 60) return `${mn} min – ${(mx/60).toFixed(mx%60===0?0:1)} h`;
  return `${mn}–${mx} min`;
};

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const fmtDuration = (mins) => {
  if (mins == null) return '';
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
};

export default function AdminPriorities({ token }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [editing, setEditing] = useState(null); // priority being edited (full doc)
  const [creating, setCreating] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/priorities/`, { headers });
      if (r.ok) setList(await r.json());
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const toggle = async (p) => {
    setSaving(p.id);
    try {
      const r = await fetch(`${API_URL}/api/priorities/${p.id}/toggle`, { method: 'POST', headers });
      if (r.ok) load();
    } finally { setSaving(null); }
  };

  const updateEnabled = async (p, enabled) => {
    setSaving(p.id);
    try {
      const r = await fetch(`${API_URL}/api/priorities/${p.id}`, {
        method: 'PATCH', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (r.ok) load();
    } finally { setSaving(null); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete priority "${p.name}"? Existing orders that used it will keep the label, but customers can no longer pick it.`)) return;
    const r = await fetch(`${API_URL}/api/priorities/${p.id}`, { method: 'DELETE', headers });
    if (r.ok) load();
  };

  const save = async () => {
    if (!editing) return;
    const body = { ...editing };
    delete body.id; delete body.created_at; delete body.updated_at; delete body.available_now;
    setSaving(editing.id || 'new');
    try {
      const url = editing.id
        ? `${API_URL}/api/priorities/${editing.id}`
        : `${API_URL}/api/priorities/`;
      const method = editing.id ? 'PATCH' : 'POST';
      const r = await fetch(url, { method, headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) {
        const t = await r.text();
        alert('Save failed: ' + t);
        return;
      }
      setEditing(null);
      setCreating(false);
      load();
    } finally { setSaving(null); }
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto" data-testid="admin-priorities-page">
      <PageHeader
        icon={<Zap className="w-5 h-5" />}
        title="Order Priorities"
        subtitle="Configure ETAs, price factors, and offline schedules. Customers see only enabled & available priorities."
        testid="admin-priorities-header"
        actions={
          <button onClick={() => { setEditing({ ...blank }); setCreating(true); }} data-testid="add-priority-btn"
            className="px-3 sm:px-4 py-2 rounded-lg font-medium bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-lg transition-all flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add Priority</span>
          </button>
        }
      />

      {loading ? (
        <p className="text-center text-slate-500 py-12"><Loader2 className="w-5 h-5 inline animate-spin mr-2" />Loading…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((p) => {
            const colorBg = COLOR_BG[p.color] || COLOR_BG.blue;
            const unavailable = !p.available_now;
            return (
              <div key={p.id} className={`bg-white rounded-xl border ${unavailable ? 'border-amber-300' : 'border-slate-200'} shadow-sm hover:shadow-md transition-shadow overflow-hidden`} data-testid={`priority-card-${p.key}`}>
                <div className={`${colorBg} text-white p-4 flex items-center justify-between`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-3xl">{p.icon}</span>
                    <div className="min-w-0">
                      <h3 className="text-lg font-bold flex items-center gap-2">
                        {p.name}
                        {p.is_default && <Star className="w-4 h-4 text-yellow-200" title="Default" />}
                      </h3>
                      <p className="text-xs opacity-90 truncate">{fmtETA(p.eta_min, p.eta_max)} · ×{p.factor.toFixed(2)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => toggle(p)}
                    disabled={saving === p.id}
                    data-testid={`toggle-priority-${p.key}`}
                    title={p.offline ? 'Currently offline — click to bring online' : 'Click to toggle offline'}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${p.offline ? 'bg-white/30 hover:bg-white/40' : 'bg-white/10 hover:bg-white/20'}`}
                  >
                    {saving === p.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <Power className={`w-5 h-5 ${p.offline ? 'text-amber-100' : 'text-white'}`} />}
                  </button>
                </div>

                <div className="p-4 space-y-3">
                  <p className="text-sm text-slate-600 line-clamp-2 min-h-[40px]">{p.description || <span className="italic text-slate-400">No description</span>}</p>

                  {unavailable && (
                    <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{p.unavailable_reason || (p.offline ? (p.offline_message || 'Currently offline.') : 'Out of working hours.')}</span>
                    </div>
                  )}

                  {p.next_transition && !p.offline && (
                    <div
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[11px] font-medium border ${
                        p.next_transition.to_available
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                          : 'bg-rose-50 border-rose-200 text-rose-800'
                      }`}
                      title={p.next_transition.reason || ''}
                      data-testid={`next-transition-${p.key}`}
                    >
                      <ArrowRight className={`w-3.5 h-3.5 flex-shrink-0 ${p.next_transition.to_available ? 'text-emerald-600' : 'text-rose-600'}`} />
                      <span>
                        {p.next_transition.to_available ? 'Becomes available' : 'Goes offline'} at{' '}
                        <strong>{p.next_transition.at}</strong>
                        {p.next_transition.weekday_offset > 0 && (
                          <span className="text-slate-500"> ({DAY_LABELS[(new Date().getDay() + 6 + p.next_transition.weekday_offset) % 7]})</span>
                        )}
                        {' · in '}<strong>{fmtDuration(p.next_transition.in_minutes)}</strong>
                      </span>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-2 text-slate-600"><Clock className="w-3.5 h-3.5" /> ETA: <span className="font-semibold text-slate-800">{fmtETA(p.eta_min, p.eta_max)}</span></div>
                    <div className="flex items-center gap-2 text-slate-600">× factor: <span className="font-semibold text-slate-800">{p.factor.toFixed(2)}</span></div>
                    {p.always_available ? (
                      <div className="col-span-2 flex items-center gap-2 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded text-emerald-700 font-semibold">
                        ● Always available (24/7)
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 text-slate-600">Open offset: <span className="font-semibold text-slate-800">{p.time_to_open || 0} min</span></div>
                        <div className="flex items-center gap-2 text-slate-600">Close offset: <span className="font-semibold text-slate-800">{p.time_to_close || 0} min</span></div>
                      </>
                    )}
                  </div>

                  <div className="text-xs">
                    <div className="flex items-start gap-2 text-slate-600">
                      <Calendar className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <span className="font-medium">Disable windows:</span>{' '}
                        {(p.disable_from || []).length === 0 ? (
                          <span className="text-slate-400 italic">none</span>
                        ) : (
                          <span className="inline-flex flex-wrap gap-1 align-middle" data-testid={`disable-windows-${p.key}`}>
                            {(p.disable_from || []).map((from, i) => {
                              const to = (p.disable_to || [])[i] || '00:00';
                              return (
                                <span key={i} className="px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 font-mono text-[11px]">
                                  {from}–{to}
                                </span>
                              );
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input type="checkbox" checked={p.enabled} onChange={(e) => updateEnabled(p, e.target.checked)} data-testid={`enabled-${p.key}`} className="w-4 h-4 accent-emerald-500" />
                      <span className={p.enabled ? 'text-emerald-700 font-medium' : 'text-slate-400'}>{p.enabled ? 'Enabled' : 'Disabled'}</span>
                    </label>
                    <div className="flex gap-1">
                      <button onClick={() => setEditing({ ...p })} data-testid={`edit-priority-${p.key}`} className="p-2 rounded hover:bg-cyan-50 text-cyan-600" title="Edit"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => remove(p)} data-testid={`delete-priority-${p.key}`} className="p-2 rounded hover:bg-red-50 text-red-500" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit / Create modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => { setEditing(null); setCreating(false); }}>
          <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="priority-editor">
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-5 flex items-center justify-between">
              <h2 className="text-lg font-bold">{creating ? 'New priority' : `Edit · ${editing.name}`}</h2>
              <button onClick={() => { setEditing(null); setCreating(false); }} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Key (slug)</label>
                  <input value={editing.key} onChange={(e) => setEditing({ ...editing, key: e.target.value.toLowerCase().replace(/\s+/g, '-') })} disabled={!creating} data-testid="prio-input-key"
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg disabled:bg-slate-100 font-mono text-sm" placeholder="highest" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Display name</label>
                  <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="prio-input-name"
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg" placeholder="Highest" />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase">Description (shown to customer)</label>
                <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows="2" data-testid="prio-input-desc"
                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" placeholder="Most urgent — answered within 10–30 minutes" />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">ETA min (minutes)</label>
                  <input type="number" min="0" value={editing.eta_min} onChange={(e) => setEditing({ ...editing, eta_min: parseInt(e.target.value || 0, 10) })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">ETA max (minutes)</label>
                  <input type="number" min="0" value={editing.eta_max} onChange={(e) => setEditing({ ...editing, eta_max: parseInt(e.target.value || 0, 10) })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Price factor</label>
                  <input type="number" step="0.01" value={editing.factor} onChange={(e) => setEditing({ ...editing, factor: parseFloat(e.target.value || 0) })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg" />
                  <p className="text-[10px] text-slate-400 mt-0.5">1.20 = +20% surcharge</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Order</label>
                  <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || 0, 10) })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Color</label>
                  <select value={editing.color} onChange={(e) => setEditing({ ...editing, color: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg bg-white">
                    {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Icon (emoji or glyph)</label>
                  <input value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-lg" />
                </div>
              </div>

              {/* Working-hours-aware availability */}
              <div className="border-t border-slate-100 pt-4">
                <h4 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-500" />
                  Working-hours availability
                </h4>
                <p className="text-xs text-slate-500 mb-3">
                  Tied to global Working Hours (Settings → Working Hours). Use 0 to disable a rule.
                </p>

                <label className="flex items-start gap-2 mb-3 p-2 bg-emerald-50 border border-emerald-200 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editing.always_available}
                    onChange={(e) => setEditing({ ...editing, always_available: e.target.checked })}
                    data-testid="prio-input-always-available"
                    className="w-4 h-4 mt-0.5 accent-emerald-500"
                  />
                  <span className="text-xs text-emerald-900">
                    <strong>Always available (24/7)</strong> — ignore working hours, time-to-open and time-to-close.
                    The Offline toggle and Disable windows still apply. Recommended for the lowest / overnight priority.
                  </span>
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase">Time to open (minutes)</label>
                    <input
                      type="number" min="0"
                      value={editing.time_to_open ?? 0}
                      onChange={(e) => setEditing({ ...editing, time_to_open: parseInt(e.target.value || 0, 10) })}
                      data-testid="prio-input-time-to-open"
                      className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg" />
                    <p className="text-[10px] text-slate-400 mt-0.5">Hidden for N min after schedule open. Ex: 120 = activates 2h after open.</p>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase">Time to close (minutes)</label>
                    <input
                      type="number" min="0"
                      value={editing.time_to_close ?? 0}
                      onChange={(e) => setEditing({ ...editing, time_to_close: parseInt(e.target.value || 0, 10) })}
                      data-testid="prio-input-time-to-close"
                      className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg" />
                    <p className="text-[10px] text-slate-400 mt-0.5">Hidden N min before schedule close. Ex: 60 = hides 1h before close.</p>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold text-slate-500 uppercase">Disable windows (HH:MM today)</label>
                    <button
                      onClick={() => setEditing({
                        ...editing,
                        disable_from: [...(editing.disable_from || []), '06:00'],
                        disable_to: [...(editing.disable_to || []), '11:00'],
                      })}
                      data-testid="prio-add-disable-window"
                      className="text-xs px-3 py-1 bg-cyan-50 text-cyan-700 rounded hover:bg-cyan-100 flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Add window
                    </button>
                  </div>
                  {(editing.disable_from || []).length === 0 && (
                    <p className="text-[11px] text-slate-400 italic">No daily disable windows. Priority follows working hours only.</p>
                  )}
                  {(editing.disable_from || []).map((from, i) => (
                    <div key={i} className="flex items-center gap-2 mb-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid={`prio-disable-row-${i}`}>
                      <span className="text-xs text-slate-500 w-12">From</span>
                      <input
                        type="time"
                        value={from}
                        onChange={(e) => {
                          const arr = [...editing.disable_from];
                          arr[i] = e.target.value;
                          setEditing({ ...editing, disable_from: arr });
                        }}
                        className="px-2 py-1 border border-slate-300 rounded text-xs" />
                      <span className="text-slate-400">→</span>
                      <span className="text-xs text-slate-500 w-6">To</span>
                      <input
                        type="time"
                        value={(editing.disable_to || [])[i] || '00:00'}
                        onChange={(e) => {
                          const arr = [...(editing.disable_to || [])];
                          arr[i] = e.target.value;
                          setEditing({ ...editing, disable_to: arr });
                        }}
                        className="px-2 py-1 border border-slate-300 rounded text-xs" />
                      <button
                        onClick={() => {
                          const fromArr = editing.disable_from.filter((_, idx) => idx !== i);
                          const toArr = (editing.disable_to || []).filter((_, idx) => idx !== i);
                          setEditing({ ...editing, disable_from: fromArr, disable_to: toArr });
                        }}
                        className="ml-auto p-1 text-red-500 hover:bg-red-50 rounded"
                        title="Remove window">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-6 pt-2 border-t border-slate-100">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} className="w-4 h-4 accent-emerald-500" />
                  Enabled
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editing.is_default} onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })} className="w-4 h-4 accent-yellow-500" />
                  Default selection
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editing.offline} onChange={(e) => setEditing({ ...editing, offline: e.target.checked })} className="w-4 h-4 accent-amber-500" />
                  Currently offline
                </label>
              </div>

              {editing.offline && (
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Offline message</label>
                  <input value={editing.offline_message || ''} onChange={(e) => setEditing({ ...editing, offline_message: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg" placeholder="I'm not online right now — please pick another priority." />
                </div>
              )}

              <div className="border-t border-slate-100 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-bold text-slate-700">Recurring off-hours</h4>
                  <button onClick={() => setEditing({ ...editing, unavailable_schedule: [...(editing.unavailable_schedule || []), { days: [0,1,2,3,4], start: '22:00', end: '06:00', label: '' }] })}
                    className="text-xs px-3 py-1 bg-cyan-50 text-cyan-700 rounded hover:bg-cyan-100 flex items-center gap-1"><Plus className="w-3 h-3" /> Add window</button>
                </div>
                {(editing.unavailable_schedule || []).map((s, idx) => (
                  <div key={idx} className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {DAYS.map((d) => (
                        <button key={d.v} onClick={() => {
                          const days = s.days.includes(d.v) ? s.days.filter((x) => x !== d.v) : [...s.days, d.v].sort();
                          const arr = [...editing.unavailable_schedule];
                          arr[idx] = { ...s, days };
                          setEditing({ ...editing, unavailable_schedule: arr });
                        }} className={`px-2 py-1 rounded text-xs font-medium ${s.days.includes(d.v) ? 'bg-cyan-500 text-white' : 'bg-white border border-slate-300 text-slate-600'}`}>
                          {d.label}
                        </button>
                      ))}
                      <input type="time" value={s.start} onChange={(e) => { const arr = [...editing.unavailable_schedule]; arr[idx] = { ...s, start: e.target.value }; setEditing({ ...editing, unavailable_schedule: arr }); }}
                        className="px-2 py-1 border border-slate-300 rounded text-xs" />
                      <span className="text-slate-400">→</span>
                      <input type="time" value={s.end} onChange={(e) => { const arr = [...editing.unavailable_schedule]; arr[idx] = { ...s, end: e.target.value }; setEditing({ ...editing, unavailable_schedule: arr }); }}
                        className="px-2 py-1 border border-slate-300 rounded text-xs" />
                      <input value={s.label || ''} onChange={(e) => { const arr = [...editing.unavailable_schedule]; arr[idx] = { ...s, label: e.target.value }; setEditing({ ...editing, unavailable_schedule: arr }); }}
                        placeholder="Label (e.g. Lunch)" className="px-2 py-1 border border-slate-300 rounded text-xs flex-1 min-w-[100px]" />
                      <button onClick={() => { const arr = editing.unavailable_schedule.filter((_, i) => i !== idx); setEditing({ ...editing, unavailable_schedule: arr }); }}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
                {(editing.unavailable_schedule || []).length === 0 && (
                  <p className="text-xs text-slate-400 italic">No off-hours configured. Priority is always available unless manually set offline.</p>
                )}
              </div>
            </div>

            <div className="bg-slate-50 px-5 py-4 flex justify-end gap-2 border-t border-slate-200">
              <button onClick={() => { setEditing(null); setCreating(false); }} className="px-5 py-2 bg-white border border-slate-300 rounded-lg font-medium hover:bg-slate-100">Cancel</button>
              <button onClick={save} disabled={saving === editing.id || saving === 'new' || !editing.key || !editing.name} data-testid="save-priority-btn"
                className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg font-medium disabled:opacity-50 hover:shadow-md flex items-center gap-2">
                {(saving === editing.id || saving === 'new') ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save priority
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
